/* Copyright (c) 2020-present CloudIO Inc */

import * as dsTypes from '@cloudio-saas/datasource-types';
import * as Mutx from '@cloudio-saas/mutx';
import * as StateX from '@cloudio-saas/statex';
import * as ChartJS from 'chart.js';
import * as MUILab from '../mui-lab';
import * as MUI from '@mui/material';
import * as MUIStyles from '@mui/styles';
import * as ReactDropZone from 'react-dropzone';
import * as d3 from 'd3';
import clsx from 'clsx';
import { Platform } from '../componentregistry';
import {
  add,
  addDays,
  differenceInDays,
  endOfDay,
  format,
  formatDistanceToNow,
  isAfter,
  isBefore,
  isDate,
  isFriday,
  isFuture,
  isMonday,
  isPast,
  isSameDay,
  isSaturday,
  isSunday,
  isThursday,
  isToday,
  isTuesday,
  isValid,
  isWednesday,
  isWeekend,
  parseISO,
  startOfDay,
  sub,
  subDays,
  subMonths,
} from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import * as React from 'react';
import { ulid } from 'ulid';
import { getErrorMessage } from '../utils/isEmpty';
import Logger from '../utils/Logger';
import type { CallResult, ControllerInterface } from './ControllerInterface';
import {
  controllerFunctionsAtom,
  getController,
  registerController,
} from './ControllerRegistry';
import * as ReactWindow from 'react-window';
import { transform } from 'web_worker';
import { Theme } from '@mui/material';
import axios from 'axios';

// workaround for Cannot access '__WEBPACK_DEFAULT_EXPORT__' before initialization
const cloudioImport = import('../cloudio');

const getRequireBase =
  ({
    platform,
    appUid,
    cloudio,
  }: {
    platform: Platform;
    appUid: string;
    cloudio: any;
  }) =>
  (moduleName: string) => {
    switch (moduleName) {
      case 'platform':
        return platform;
      case 'react':
        return React;
      case '@mui/material':
        return MUI;
      case 'react-dropzone':
        return ReactDropZone;
      case '@mui/styles':
        return MUIStyles;
      case 'axios':
        return axios;
      case 'chart.js':
        return ChartJS;
      case '@mui/lab':
        return MUILab;
      case '@cloudio-saas/datasource-types':
        return dsTypes;
      case '@cloudio-saas/statex':
        return StateX;
      case '@cloudio-saas/mutx':
        return Mutx;
      case 'uuid':
      case 'ulid':
        return ulid;
      case 'clsx':
        return clsx;
      case 'cloudio':
        return cloudio;
      case 'framer-motion':
        return { motion, AnimatePresence };
      case 'date-fns':
        return {
          add,
          addDays,
          differenceInDays,
          endOfDay,
          format,
          formatDistanceToNow,
          isAfter,
          isBefore,
          isDate,
          isFriday,
          isFuture,
          isMonday,
          isPast,
          isSameDay,
          isSaturday,
          isSunday,
          isThursday,
          isToday,
          isTuesday,
          isValid,
          isWednesday,
          isWeekend,
          parseISO,
          startOfDay,
          sub,
          subDays,
          subMonths,
        };
      case 'app':
        return getController(appUid, 'AppController');
      case 'react-window':
        return ReactWindow;
      case 'd3':
        return d3;
      default:
        throw Error(`You can't use module ${moduleName} in the controller.`);
    }
  };

export interface BaseFunctionProps extends Record<string, unknown> {
  platform: Platform;
  appUid: string;
  pageId: string;
  itemId: string;
  functionName: string;
  theme: Theme;
}

interface CallProps {
  platform: Platform;
  appUid: string;
  pageId: string;
  itemId: string;
  functionName: string;
  props: Record<string, unknown>;
}

class Controller {
  static async compile({
    appUid,
    pageId,
    platform,
    source,
    fileName,
    debug = false,
    instant = false,
  }: {
    appUid: string;
    pageId: string;
    platform: Platform;
    source?: string;
    fileName: string;
    debug?: boolean;
    instant?: boolean;
  }): Promise<boolean> {
    let isSuccess = true;
    if (!source) {
      registerController(appUid, pageId, {});
      platform.setter(controllerFunctionsAtom, [], {
        params: { appUid, pageId },
      });
      return isSuccess;
    }
    const cloudio = await cloudioImport;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const require = getRequireBase({ appUid, platform, cloudio });
    let result: { code?: string | null; error?: string } = {};
    try {
      result = await Controller.transform(fileName, source, debug);
      if (result.code) {
        const exports: { default?: ControllerInterface } = {};
        const module = { exports };
        // eslint-disable-next-line no-eval
        eval(result.code);
        if (module.exports.default) {
          registerController(appUid, pageId, module.exports.default);
          platform.setter(
            controllerFunctionsAtom,
            Object.keys(module.exports.default),
            { params: { appUid, pageId } },
          );
        }
      } else {
        isSuccess = false;
        if (!instant) {
          setTimeout(
            () =>
              result.error &&
              platform.showError(
                `Developer Exception in ${pageId} Page Controller\n${result.error}`,
              ),
            10,
          );
        }
      }
    } catch (e) {
      isSuccess = false;
      if (!instant) {
        if (console) {
          console.error(source);
          console.error(result.code);
          console.error(e);
        }
        setTimeout(
          () =>
            platform.showError(
              `Developer Exception in ${pageId} Page Controller\n${getErrorMessage(
                e,
              )}`,
            ),
          10,
        );
      }
    }
    return isSuccess;
  }

  static async transform(
    name: string,
    source: string,
    debug: boolean,
  ): Promise<{ code?: string | null; error?: string }> {
    try {
      const { code, error } = await transform(name, source, debug);
      if (error) {
        return { error: getErrorMessage(error) };
      }
      return { code };
    } catch (e) {
      if (console) console.error(e);
      return { error: getErrorMessage(e) };
    }
  }

  static callSync<T>({
    appUid,
    functionName,
    itemId,
    pageId,
    platform,
    props,
  }: CallProps): CallResult<T> {
    const inst = getController(appUid, pageId);
    if (inst && typeof inst[functionName] === 'function') {
      try {
        if (Logger.isLogEnabled()) {
          Logger.log({
            itemType: functionName,
            status: 'calling',
          });
        }
        const result = inst[functionName]({
          appUid,
          functionName,
          itemId,
          pageId,
          platform,
          theme: platform.theme,
          ...props,
        }) as T;
        if (!StateX.isPromise(result)) {
          return { status: 'SUCCESS', result };
        }
        platform.showError(
          `Developer Exception in ${functionName} function\nFunction returned a promise when a non-promise value is expected.`,
        );
      } catch (error) {
        Logger.log({
          itemType: functionName,
          status: 'error',
          msg: getErrorMessage(error),
        });
        if (console) console.error(error);
        platform.showError(
          `Developer Exception in ${functionName} function\n${getErrorMessage(
            error,
          )}`,
        );
      }
    } else {
      platform.showError(
        `Developer Exception: Controller function ${functionName} is missing!`,
      );
    }
    return { status: 'ERROR' };
  }

  static callSyncIfExists<T>(props: CallProps): CallResult<T> {
    const { functionName, appUid, pageId } = props;
    const inst = getController(appUid, pageId);
    if (inst?.[functionName]) {
      return this.callSync(props);
    }
    return { status: 'NONE' };
  }

  static async callAsync<T>({
    appUid,
    functionName,
    itemId,
    pageId,
    platform,
    props,
  }: CallProps): Promise<CallResult<T>> {
    const inst = getController(appUid, pageId);
    if (inst && typeof inst[functionName] === 'function') {
      try {
        if (Logger.isLogEnabled()) {
          Logger.log({
            itemType: functionName,
            status: 'calling',
          });
        }
        const result = (await inst[functionName]({
          appUid,
          functionName,
          itemId,
          pageId,
          platform,
          theme: platform.theme,
          ...props,
        })) as T;
        return { status: 'SUCCESS', result };
      } catch (error) {
        Logger.log({
          itemType: functionName,
          status: 'error',
          msg: getErrorMessage(error),
        });
        if (console) console.error(error);
        platform.showError(
          `Developer Exception in ${functionName} function<br>${getErrorMessage(
            error,
          )}`,
        );
      }
    } else {
      platform.showError(
        `Developer Exception: Controller function ${functionName} is missing!`,
      );
    }
    return { status: 'ERROR' };
  }

  static async callAsyncIfExists<T>(props: CallProps): Promise<CallResult<T>> {
    const { functionName, appUid, pageId } = props;
    const inst = getController(appUid, pageId);
    if (inst?.[functionName]) {
      return this.callAsync(props);
    }
    return { status: 'NONE' };
  }

  static getExport<T>(
    appUid: string,
    pageId: string,
    key: string,
  ): T | undefined {
    const inst = getController(appUid, pageId);
    return inst ? (inst[key] as unknown as T) : undefined;
  }
}

export default Controller;
